import React from "react";
import ReactDOM from "react-dom/client";
import "./scss";
import { BrowserRouter } from "react-router-dom";
import Loading from "components/Loading";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "utils/queryClient";

const App = React.lazy(() => import("./App"));

// Patch ResizeObserver to prevent "loop completed with undelivered notifications"
// This benign browser error triggers React's dev overlay, especially during
// screen sharing (Slack/Zoom) + DevTools which cause rapid viewport resizes.
if (typeof window !== "undefined" && typeof ResizeObserver !== "undefined") {
  const OrigRO = ResizeObserver;
  // @ts-ignore
  window.ResizeObserver = class PatchedResizeObserver extends OrigRO {
    constructor(cb: ResizeObserverCallback) {
      let rafId = 0;
      super((entries, observer) => {
        // Cancel any pending callback — only the latest resize matters
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          try { cb(entries, observer); } catch (_) {}
        });
      });
    }
  };
  // Suppress the error if it still leaks through
  window.addEventListener("error", (e) => {
    if (e.message?.includes?.("ResizeObserver loop")) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
    }
  }, true);
}

// A deploy can replace static chunks while a user still has an old index.html
// open; their next lazy import 404s (or resolves to the SPA fallback HTML,
// surfacing as a JS syntax error). Reload once to pick up the new build
// instead of leaving the user on a broken screen.
if (typeof window !== "undefined") {
  const isChunkLoadFailure = (err: unknown): boolean => {
    if (!err) return false;
    const name = (err as { name?: string }).name;
    const message = (err as { message?: string }).message ?? String(err);
    return (
      name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/.test(message) ||
      /Failed to fetch dynamically imported module/.test(message) ||
      /Unexpected token '<'/.test(message)
    );
  };

  const reloadOnceForStaleChunk = (err: unknown) => {
    if (!isChunkLoadFailure(err)) return;
    const key = "chunk-reload-attempted";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    window.location.reload();
  };

  window.addEventListener("error", (e) => reloadOnceForStaleChunk(e.error ?? e));
  window.addEventListener("unhandledrejection", (e) => reloadOnceForStaleChunk(e.reason));
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const InitialLoader = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}
  >
    <Loading />
  </div>
);

root.render(
  <BrowserRouter>
    <React.Suspense fallback={<InitialLoader />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.Suspense>
  </BrowserRouter>
);

if ("serviceWorker" in navigator) {
  if (process.env.NODE_ENV === "production") {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          console.log("Service Worker registered successfully:", registration);
        })
        .catch((error) => {
          console.log("Failed to register Service Worker:", error);
        });
    });
  } else {
    // Dev: a caching service worker serves a STALE bundle and masks every rebuild
    // (incl. .env/API-URL changes), which looks like "the backend is broken". Never
    // run it in dev — and proactively unregister any SW a previous build installed.
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
  }
}
